import { useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router'

import { RouteId } from 'core/routing/router/route-ids'
import { getRouteIdFromPath } from 'core/utils/route-utils'
import { useGlobalFiltersStore } from './useGlobalFiltersStore'
import { useStoreTrajectoryFiltersStore } from './useStoreTrajectoryFiltersStore'

const STORE_ID_PARAM = 'storeId'
const YEAR_PARAM = 'year'
const REFERENCE_STORE_ID_PARAM = 'referenceStoreId'

/**
 * Synchronize trajectory filters between store and URL query parameters.
 * `year` is only kept on the edit route; the list URL only carries `storeId`.
 */
export const useStoreTrajectoryFiltersUrlSync = () => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const storeId = useGlobalFiltersStore(state => state.storeId)
  const targetYear = useStoreTrajectoryFiltersStore(state => state.targetYear)
  const setStoreId = useGlobalFiltersStore(state => state.setStoreId)
  const setTargetYear = useStoreTrajectoryFiltersStore(state => state.setTargetYear)
  const referenceStoreId = useStoreTrajectoryFiltersStore(state => state.referenceStoreId)
  const setReferenceStoreId = useStoreTrajectoryFiltersStore(state => state.setReferenceStoreId)

  const isWithYearRoute = [
    RouteId.StoreTrajectoriesEdit,
    RouteId.StoreTrajectoriesValidate,
    RouteId.StoreTrajectoriesConsult
  ].includes(getRouteIdFromPath(location.pathname))

  const isWithReferenceStoreRoute = RouteId.StoreTrajectoriesEdit === getRouteIdFromPath(location.pathname)

  useEffect(() => {
    const urlStoreId = searchParams.get(STORE_ID_PARAM)
    const urlYear = searchParams.get(YEAR_PARAM)
    const urlReferenceStoreId = searchParams.get(REFERENCE_STORE_ID_PARAM)

    if (urlStoreId !== null) {
      const parsedStoreId = Number.parseInt(urlStoreId, 10)
      if (!Number.isNaN(parsedStoreId)) {
        setStoreId(parsedStoreId)
      }
    }

    if (isWithYearRoute && urlYear !== null) {
      const parsedYear = Number.parseInt(urlYear, 10)
      if (!Number.isNaN(parsedYear)) {
        setTargetYear(parsedYear)
      }
    }

    if (isWithReferenceStoreRoute && urlReferenceStoreId !== null) {
      const parsedReferenceStoreId = Number.parseInt(urlReferenceStoreId, 10)
      if (!Number.isNaN(parsedReferenceStoreId)) {
        setReferenceStoreId(parsedReferenceStoreId)
      }
    }
  }, [])

  const yearForUrlSync = isWithYearRoute ? targetYear : null
  const referenceStoreIdForUrlSync = isWithReferenceStoreRoute ? referenceStoreId : null

  useEffect(() => {
    setSearchParams(
      previousParams => {
        const nextParams = new URLSearchParams(previousParams)

        if (storeId === null) {
          nextParams.delete(STORE_ID_PARAM)
        } else {
          nextParams.set(STORE_ID_PARAM, String(storeId))
        }

        if (yearForUrlSync === null) {
          nextParams.delete(YEAR_PARAM)
        } else {
          nextParams.set(YEAR_PARAM, String(yearForUrlSync))
        }

        if (referenceStoreIdForUrlSync === null) {
          nextParams.delete(REFERENCE_STORE_ID_PARAM)
        } else {
          nextParams.set(REFERENCE_STORE_ID_PARAM, String(referenceStoreIdForUrlSync))
        }

        return nextParams
      },
      { replace: true }
    )
  }, [setSearchParams, storeId, yearForUrlSync, referenceStoreIdForUrlSync])
}
